/**
 * `.gproj` の型と経路（§3.1）。
 *
 * | 版 | 配列本体 | 注釈 |
 * |----|----------|------|
 * | v1 `1.0.0` | `sequences/0001_primary.bin` | `features/annotations.json` |
 * | v2 `2.0.0` | `sequences/{id}.bin` | `features/{id}.json` |
 * | v3 `3.0.0` | `sequences/{id}/{000000}.bin`（固定長チャンク） | `features/{id}.json` |
 *
 * v3 は染色体級（249 Mbp）を単一エントリにしないための版（§0.1）。読みは v1/v2 も受ける。
 */

import { GenomicFeatureV1 } from '../annotation/types';
import { Topology } from '../sequence/interval';

export const GPROJ_FORMAT_VERSION_V1 = '1.0.0';
export const GPROJ_FORMAT_VERSION_V2 = '2.0.0';
export const GPROJ_FORMAT_VERSION_V3 = '3.0.0';

export const MANIFEST_PATH = 'manifest.json';
export const V1_SEQUENCE_PATH = 'sequences/0001_primary.bin';
export const V1_FEATURES_PATH = 'features/annotations.json';

export function chunkEntryPath(sequenceId: string, chunkIndex: number): string {
  return `sequences/${sequenceId}/${String(chunkIndex).padStart(6, '0')}.bin`;
}

export function featuresEntryPath(sequenceId: string): string {
  return `features/${sequenceId}.json`;
}

export interface GprojSequenceEntry {
  id: string;
  name: string;
  description?: string;
  length: number;
  topology: Topology;
  chunkSize: number;
  chunkCount: number;
  /** チャンク Merkle 根（`checksumAlgorithm` 参照） */
  checksum?: string;
  checksumAlgorithm?: string;
}

export interface GprojManifest {
  formatVersion: string;
  generator?: string;
  project: {
    id: string;
    name: string;
    createdAt: string;
    updatedAt: string;
  };
  sequences: GprojSequenceEntry[];
  uiPreferences?: Record<string, unknown>;
}

export interface GprojFeatureFile {
  sequenceId: string;
  features: GenomicFeatureV1[];
}

export class GprojFormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GprojFormatError';
  }
}

/** ZIP エントリ名の検査（`..` / 絶対パス拒否・§3.1） */
export function assertSafeEntryPath(path: string): void {
  if (path.length === 0 || path.length > 512) {
    throw new GprojFormatError(`ZIP エントリ名が不正です: ${path}`);
  }
  if (path.startsWith('/') || path.startsWith('\\') || /^[a-zA-Z]:/.test(path)) {
    throw new GprojFormatError(`絶対パスのエントリは受け付けません: ${path}`);
  }
  if (path.split(/[\\/]/).some((segment) => segment === '..')) {
    throw new GprojFormatError(`親ディレクトリ参照のエントリは受け付けません: ${path}`);
  }
}
